import * as React from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface FieldProps {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  optional?: boolean;
  error?: React.ReactNode;
  htmlFor?: string;
  children: React.ReactElement | ((id: string) => React.ReactElement);
  className?: string;
}

// Wraps a control with label, hint, and error text. The control's `id` is
// injected so the `<label>` association is explicit (works for inputs,
// selects, radio groups, anything with an `id`). Callers can:
//   - pass a single element and we'll inject `id` for them, OR
//   - pass a render function `(id) => ReactElement` for full control.
export function Field({
  label,
  hint,
  optional,
  error,
  htmlFor,
  children,
  className,
}: FieldProps) {
  const { t } = useTranslation();
  const generated = useId();
  const id = htmlFor ?? generated;

  const control =
    typeof children === 'function'
      ? children(id)
      : React.cloneElement(children, { id: children.props.id ?? id });

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label
          htmlFor={id}
          className="flex items-baseline justify-between text-xs text-ink-2 tracking-[0.01em]"
        >
          <span>{label}</span>
          {optional && <span className="text-[11px] text-muted-2">{t('common.optional').toLowerCase()}</span>}
        </label>
      )}
      {control}
      {hint && !error && <span className="text-[11.5px] text-muted">{hint}</span>}
      {error && <span className="text-[11.5px] text-destructive">{error}</span>}
    </div>
  );
}
